import React, { useState, useEffect } from 'react';
import {
  Card, Input, Button, Select, Tag, Space, Tabs, List, Typography,
  Modal, Form, message, Statistic, Row, Col, Divider, Empty, Tooltip,
  Popconfirm,
} from 'antd';
import {
  BulbOutlined, SearchOutlined, CopyOutlined, LikeOutlined,
  DislikeOutlined, MailOutlined, DatabaseOutlined, ThunderboltOutlined,
  PlusOutlined, ImportOutlined, RobotOutlined,
} from '@ant-design/icons';
import { strategyApi } from '../services/api';
import { fmtCN } from '../utils/time';

const ROLE_LABEL = {
  admin: '管理员',
  dispatcher: '调度',
  dispatch: '调度',
  sales: '销售',
  customer_service: '客服',
  service: '客服',
};
const roleLabel = (r) => ROLE_LABEL[r] || r || '';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

// 问题类型默认枚举（前端 fallback 双保险：即使后端 GET 未合并 defaults，也能显示完整选项）
const DEFAULT_PROBLEM_TYPES = [
  "滞留", "破损", "拒收", "未提取", "丢货", "时效延误",
  "异常签收", "空跑", "尾板卸货", "超重超长", "预约/派送",
  "仓储费", "滞港费", "单证缺失", "客户投诉", "其他",
];

export default function StrategyAnalysis() {
  const [activeTab, setActiveTab] = useState('query');

  // 查询相关
  const [queryText, setQueryText] = useState('');
  const [queryProblemType, setQueryProblemType] = useState('');
  const [querying, setQuerying] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  // 问题类型：初始化即有 fallback，后端合并后再扩展（保证即使后端接口/进程未升级也有16项默认）
  const [problemTypes, setProblemTypes] = useState(DEFAULT_PROBLEM_TYPES);

  // 邮件导入
  const [threadModalOpen, setThreadModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [threadForm] = Form.useForm();
  const [batchText, setBatchText] = useState('');
  const [importing, setImporting] = useState(false);

  // 策略录入
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [caseKeyword, setCaseKeyword] = useState('');
  // 录入/编辑案例
  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);  // null=新建；对象=编辑
  const [caseSubmitting, setCaseSubmitting] = useState(false);
  const [caseForm] = Form.useForm();

  // 反馈统计
  const [stats, setStats] = useState({ total_feedback: 0, inaccurate_count: 0, processed_count: 0, improvement_rate: 0 });
  // 后端 AI 学习沉淀统计（管理员在后台标记的邮件经验 → 直接注入本模块 LLM 查询）
  const [learnStats, setLearnStats] = useState({ total: 0, good: 0, preferred: 0, archive: 0, bad: 0, pinned: 0 });

  useEffect(() => {
    fetchProblemTypes();
    fetchStats();
    fetchLearnStats();
    fetchCases();
  }, []);

  const fetchProblemTypes = async () => {
    try {
      const data = await strategyApi.problemTypes();
      const list = Array.isArray(data) ? data : [];
      // 合并：默认枚举在前 + 后端新增项在后，去重保序（任何情况下都至少有16项默认显示）
      const merged = Array.from(new Set([...DEFAULT_PROBLEM_TYPES, ...list]));
      setProblemTypes(merged);
    } catch (e) {
      // 接口异常时回退 DEFAULT_PROBLEM_TYPES（不变动初始值）
      setProblemTypes(DEFAULT_PROBLEM_TYPES);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await strategyApi.feedbackStats();
      setStats(data);
    } catch (e) {}
  };

  const fetchLearnStats = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token') || '';
      const h = { Authorization: `Bearer ${token}` };
      const [mk, cm] = await Promise.all([
        fetch('/api/emails/ai-learning/markers?module=strategy&page_size=1', { headers: h }).then(r => r.ok ? r.json() : { total: 0, stats: {} }),
        fetch('/api/emails/ai-learning/context-memories?module=strategy&pinned_only=true&page_size=1', { headers: h }).then(r => r.ok ? r.json() : { pinned_count: 0 }),
      ]);
      setLearnStats({
        total: mk.total || 0,
        good: (mk.stats && mk.stats.good) || 0,
        preferred: (mk.stats && mk.stats.preferred) || 0,
        archive: (mk.stats && mk.stats.archive) || 0,
        bad: (mk.stats && mk.stats.bad) || 0,
        pinned: cm.pinned_count || 0,
      });
    } catch (e) {}
  };

  const fetchCases = async () => {
    setCasesLoading(true);
    try {
      const params = { page_size: 50 };
      if (caseKeyword) params.keyword = caseKeyword;
      const data = await strategyApi.listCases(params);
      setCases(data.items);
    } catch (e) {} finally {
      setCasesLoading(false);
    }
  };

  const handleQuery = async () => {
    if (!queryText.trim()) {
      message.warning('请输入要查询的问题或场景');
      return;
    }
    setQuerying(true);
    setQueryResult(null);
    try {
      const data = await strategyApi.query({
        query: queryText,
        problem_type: queryProblemType,
        top_k: 5,
      });
      setQueryResult(data);
      if (data.results.length === 0) {
        message.info('未找到匹配的历史案例，可参考AI综合建议');
      }
    } catch (e) {} finally {
      setQuerying(false);
    }
  };

  const handleFeedback = async (caseId, strategyIndex, strategyContent, type) => {
    try {
      await strategyApi.feedback({
        case_id: caseId,
        strategy_index: strategyIndex,
        strategy_content: strategyContent,
        feedback_type: type,
        user_input_context: queryText,
      });
      if (type === 'used') message.success('已记录使用，感谢反馈');
      if (type === 'copied') message.success('已复制并记录');
      if (type === 'inaccurate') {
        message.info('已标记不准确，系统将自动分析改进');
        fetchStats();
      }
    } catch (e) {}
  };

  const handleCopy = (text, caseId, strategyIndex, strategyContent) => {
    navigator.clipboard.writeText(text);
    handleFeedback(caseId, strategyIndex, strategyContent, 'copied');
  };

  const handleCreateThread = async (values) => {
    try {
      const messages = values.messages?.split('\n---\n').map((m, i) => {
        const lines = m.trim().split('\n');
        const sender = lines.find(l => l.startsWith('发件人:'))?.replace('发件人:', '').trim() || '';
        const recipient = lines.find(l => l.startsWith('收件人:'))?.replace('收件人:', '').trim() || '';
        const subject = lines.find(l => l.startsWith('主题:'))?.replace('主题:', '').trim() || values.subject;
        const body = lines.filter(l => !l.startsWith('发件人:') && !l.startsWith('收件人:') && !l.startsWith('主题:') && !l.startsWith('时间:')).join('\n');
        return { sender, recipient, subject, body };
      }).filter(m => m.body) || [];

      await strategyApi.createThread({ ...values, messages });
      message.success('邮件thread已导入并开始分析');
      setThreadModalOpen(false);
      threadForm.resetFields();
    } catch (e) {}
  };

  // ====== 录入/编辑策略案例 ======
  const openCreateCase = () => {
    setEditingCase(null);
    caseForm.resetFields();
    setCaseModalOpen(true);
  };
  const openEditCase = (item) => {
    setEditingCase(item);
    caseForm.setFieldsValue({
      title: item.title,
      scenario: item.scenario,
      problem_type: item.problem_type,
      best_strategy: item.best_strategy,
      key_tactics: item.key_tactics,
    });
    setCaseModalOpen(true);
  };
  const handleDeleteCase = async (item) => {
    try {
      await strategyApi.deleteCase(item.id);
      message.success('已删除');
      fetchCases();
    } catch (e) {}
  };
  const handleCaseSubmit = async (values) => {
    // problem_type 只能从 16+ 预设枚举里选择；非预设前端拦截提示，不发自动创建请求（避免 405）
    const problemTypeVal = (values.problem_type || '').toString().trim();
    if (problemTypeVal && !problemTypes.includes(problemTypeVal)) {
      message.warning(`问题类型「${problemTypeVal}」不在预设列表中，请从下拉选择（${problemTypes.length} 项）`);
      return;
    }

    const payload = {
      title: values.title,
      scenario: values.scenario || '',
      problem_type: problemTypeVal,
      best_strategy: values.best_strategy || '',
      key_tactics: values.key_tactics || '',
    };
    setCaseSubmitting(true);
    try {
      if (editingCase) {
        await strategyApi.updateCase(editingCase.id, payload);
        message.success('修改成功，已提交等待审核');
      } else {
        await strategyApi.createCase(payload);
        message.success('已录入策略，等待后台审核通过后即可被调取使用');
      }
      setCaseModalOpen(false);
      caseForm.resetFields();
      setEditingCase(null);
      await fetchCases();
    } catch (e) {
      // 所有异常都明确提示（之前的空 catch 会让"提交按钮没反应"，用户无法定位）
      const detail = e?.response?.data?.detail || e?.message || (e instanceof Error ? e.message : String(e));
      message.error('提交失败：' + detail);
      console.error('handleCaseSubmit error', e, 'payload=', payload);
    } finally {
      setCaseSubmitting(false);
    }
  };

  const handleBatchImport = async () => {
    if (!batchText.trim()) {
      message.warning('请粘贴邮件内容');
      return;
    }
    setImporting(true);
    try {
      // 简单解析：按thread分割
      const threads = batchText.split('\n===\n').map(t => {
        const lines = t.trim().split('\n');
        const subject = lines.find(l => l.startsWith('主题:'))?.replace('主题:', '').trim() || '';
        const customer = lines.find(l => l.startsWith('客户:'))?.replace('客户:', '').trim() || '';
        const carrier = lines.find(l => l.startsWith('卡司:'))?.replace('卡司:', '').trim() || '';
        const orderNo = lines.find(l => l.startsWith('订单号:'))?.replace('订单号:', '').trim() || '';
        const body = lines.filter(l => !l.startsWith('主题:') && !l.startsWith('客户:') && !l.startsWith('卡司:') && !l.startsWith('订单号:')).join('\n');
        return {
          subject, customer_name: customer, carrier, order_no: orderNo, source: 'import',
          messages: [{ sender: '历史邮件', recipient: '', subject, body }],
        };
      }).filter(t => t.subject || t.messages[0].body);

      if (threads.length === 0) {
        message.warning('未解析到有效邮件内容');
        return;
      }

      await strategyApi.batchImportThreads(threads);
      message.success(`成功导入 ${threads.length} 个邮件thread，正在后台分析`);
      setBatchModalOpen(false);
      setBatchText('');
    } catch (e) {} finally {
      setImporting(false);
    }
  };

  const queryTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ThunderboltOutlined style={{ fontSize: 20, color: '#3E6AE1' }} />
          <Text strong style={{ fontSize: 16, fontWeight: 500 }}>智能策略查询</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>输入当前遇到的问题，系统从历史案例库中匹配最相关的处理策略</Text>
        </div>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <TextArea
            rows={3}
            placeholder="描述当前遇到的问题，例如：客户X的货物在洛杉矶码头滞留5天，卡司一直不安排提取，客户非常着急，该如何处理？"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
          <Space wrap>
            <Select
              placeholder="问题类型（可选）"
              value={queryProblemType || undefined}
              onChange={setQueryProblemType}
              style={{ width: 200 }}
              allowClear
              options={problemTypes.map(t => ({ value: t, label: t }))}
            />
            <Button type="primary" icon={<SearchOutlined />} loading={querying} onClick={handleQuery} size="large">
              查询策略
            </Button>
          </Space>
        </Space>
      </Card>

      {queryResult && (
        <>
          {queryResult.ai_suggestion && (
            <div className="ai-suggestion-box" style={{ marginBottom: 16 }}>
              <div className="title"><RobotOutlined /> AI综合建议</div>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{queryResult.ai_suggestion}</Paragraph>
            </div>
          )}

          <Title level={5} style={{ marginBottom: 12 }}>
            匹配到 {queryResult.results.length} 个历史案例策略
          </Title>

          {queryResult.results.length === 0 ? (
            <Card className="card-shadow"><Empty description="暂无匹配案例，建议手动导入邮件thread积累案例库" /></Card>
          ) : (
            queryResult.results.map((result, idx) => (
              <Card key={result.case_id} className="strategy-card card-shadow" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <Space>
                      <Tag color="blue">案例 #{result.case_id}</Tag>
                      <Tag color="purple">{result.problem_type}</Tag>
                      <Text strong>{result.title}</Text>
                    </Space>
                    {result.match_reason && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>匹配原因：{result.match_reason}</div>}
                  </div>
                  <Space size={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>准确率: {(result.accuracy_score * 100).toFixed(0)}%</Text>
                    <Tag color={result.effectiveness >= 4 ? 'green' : result.effectiveness === 3 ? 'blue' : 'orange'} style={{ marginLeft: 4 }}>
                      {result.effectiveness >= 4 ? '强推荐' : result.effectiveness === 3 ? '推荐' : result.effectiveness === 2 ? '谨慎' : '仅记录'}
                    </Tag>
                  </Space>
                </div>

                <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>场景：</Text>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{result.scenario}</div>
                </div>

                <Text strong style={{ marginBottom: 8, display: 'block' }}>可选策略：</Text>
                {(result.strategies || []).map((strategy, sIdx) => (
                  <div key={sIdx} style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Space>
                        <Tag color={strategy.effectiveness === '高' ? 'green' : strategy.effectiveness === '中' ? 'blue' : 'orange'}>
                          效果: {strategy.effectiveness || '未知'}
                        </Tag>
                        <Text strong>{strategy.title}</Text>
                      </Space>
                      <Space>
                        <Tooltip title="使用此策略">
                          <Button size="small" icon={<LikeOutlined />} onClick={() => handleFeedback(result.case_id, sIdx, strategy.content, 'used')}>
                            使用
                          </Button>
                        </Tooltip>
                        <Tooltip title="复制策略内容">
                          <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(strategy.content, result.case_id, sIdx, strategy.content)}>
                            复制
                          </Button>
                        </Tooltip>
                        <Popconfirm title="标记此策略不准确？系统将分析改进" onConfirm={() => handleFeedback(result.case_id, sIdx, strategy.content, 'inaccurate')}>
                          <Tooltip title="不准确，帮助系统改进">
                            <Button size="small" danger icon={<DislikeOutlined />}>不准确</Button>
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                    <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7 }}>{strategy.content}</Paragraph>
                  </div>
                ))}

                {result.best_strategy && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: '#52c41a' }}>最佳策略：</Text>
                    <Paragraph style={{ margin: '4px 0 0', fontSize: 13 }}>{result.best_strategy}</Paragraph>
                  </div>
                )}
                {result.key_tactics && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: '#fa8c16' }}>关键沟通技巧：</Text>
                    <Paragraph style={{ margin: '4px 0 0', fontSize: 13 }}>{result.key_tactics}</Paragraph>
                  </div>
                )}
              </Card>
            ))
          )}
        </>
      )}
    </div>
  );

  const importTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setThreadModalOpen(true)}>
            手动导入单个邮件Thread
          </Button>
          <Button icon={<ImportOutlined />} onClick={() => setBatchModalOpen(true)}>
            批量导入历史邮件
          </Button>
        </Space>
        <Paragraph style={{ marginTop: 12, marginBottom: 0, color: '#666' }}>
          导入邮件后，系统将自动分析每个thread的关键点、处理节点和结果，形成策略案例和上下文记忆，供策略查询调取。
        </Paragraph>
      </Card>
    </div>
  );

  const casesTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="搜索案例"
            prefix={<SearchOutlined />}
            value={caseKeyword}
            onChange={(e) => setCaseKeyword(e.target.value)}
            onPressEnter={fetchCases}
            style={{ width: 300 }}
            allowClear
          />
          <Button type="primary" onClick={fetchCases}>搜索</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateCase}>
            录入策略
          </Button>
        </Space>
      </Card>
      <Card className="card-shadow">
        <List
          loading={casesLoading}
          dataSource={cases}
          locale={{ emptyText: '暂无案例，请点击「录入策略」或导入邮件thread建立案例库' }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <a key="edit" onClick={() => openEditCase(item)}>编辑</a>,
              ]}
            >
              <div style={{ width: '100%' }}>
                {/* 标题行 */}
                <Space wrap>
                  <Text strong style={{ fontSize: 15 }}>{item.title}</Text>
                  <Tag color="purple">{item.problem_type || '未分类'}</Tag>
                  <Tag color="green">准确率 {(item.accuracy_score * 100).toFixed(0)}%</Tag>
                  {item.effectiveness != null && (
                    <Tag color={item.effectiveness >= 4 ? 'green' : item.effectiveness === 3 ? 'blue' : 'orange'}>
                      {item.effectiveness >= 4 ? '强推荐' : item.effectiveness === 3 ? '推荐' : item.effectiveness === 2 ? '谨慎' : '仅记录'}
                    </Tag>
                  )}
                </Space>

                {/* 来自：系统内角色显示岗位+姓名+时间；外部/无创建人显示「由AI分析生成」 */}
                <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
                  {item.created_by ? (
                    <>
                      来自：{[roleLabel(item.creator_role), item.creator_name].filter(Boolean).join(' ') || '系统用户'}
                      {item.created_at && <span style={{ marginLeft: 8 }}>{fmtCN(item.created_at)}</span>}
                    </>
                  ) : (
                    <>来自：由AI分析生成</>
                  )}
                </div>

                {/* 场景描述 */}
                {item.scenario && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3E6AE1', marginBottom: 4 }}>场景描述</div>
                    <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.7, background: '#f6f8fb', borderRadius: 6, padding: '8px 12px' }}>
                      {item.scenario}
                    </div>
                  </div>
                )}

                {/* 可选策略列表 */}
                {Array.isArray(item.strategies) && item.strategies.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3E6AE1', marginBottom: 4 }}>可选策略列表</div>
                    {item.strategies.map((s, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#333', lineHeight: 1.7, marginBottom: 6, padding: '8px 12px', background: '#fafafa', borderLeft: '3px solid #3E6AE1', borderRadius: 4 }}>
                        <div style={{ fontWeight: 600 }}>
                          {i + 1}. {s.title || `策略${i + 1}`}
                          {s.effectiveness && <Tag style={{ marginLeft: 6 }} color="blue">{s.effectiveness}</Tag>}
                        </div>
                        {s.content && <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{s.content}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* 最佳策略（自然语言补充说明） */}
                {item.best_strategy && (
                  <div style={{ marginTop: 10, fontSize: 13, color: '#555', lineHeight: 1.7 }}>
                    <Text strong style={{ color: '#52c41a' }}>推荐做法：</Text>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{item.best_strategy}</span>
                  </div>
                )}
              </div>
            </List.Item>
          )}
        />
      </Card>
    </div>
  );

  const statsTab = (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card className="card-shadow"><Statistic title="总反馈数" value={stats.total_feedback} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="card-shadow"><Statistic title="不准确标记" value={stats.inaccurate_count} valueStyle={{ color: '#ff4d4f' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="card-shadow"><Statistic title="已分析改进" value={stats.processed_count} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="card-shadow"><Statistic title="改进处理率" value={(stats.improvement_rate * 100).toFixed(1)} suffix="%" valueStyle={{ color: '#3E6AE1' }} /></Card>
        </Col>
      </Row>
      <Card className="card-shadow" style={{ marginTop: 16 }}>
        <Title level={5}>系统自学习机制说明</Title>
        <Paragraph>
          1. 每次策略查询返回3-5个策略，每个策略有「使用」「复制」「不准确」三个按钮<br />
          2. 点击「使用」或「复制」：增加该案例的使用计数和准确率评分<br />
          3. 点击「不准确」：系统自动分析该策略为什么不准确，生成改进建议，并将正确处理方式追加到案例经验中<br />
          4. 准确率采用贝叶斯平均算法，避免小样本波动，确保排序稳定<br />
          5. 随着使用量增加，系统会越来越准确地匹配最适合当前场景的处理策略
        </Paragraph>
      </Card>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>调度策略分析</h2>
        <p className="desc">基于历史邮件和实操经验形成策略录入，遇到问题时实时调取，越用越聪明</p>
      </div>

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}><Statistic title="策略案例" value={cases.length} prefix={<DatabaseOutlined />} /></Col>
          <Col span={6}><Statistic title="总反馈" value={stats.total_feedback} prefix={<LikeOutlined />} /></Col>
          <Col span={6}><Statistic title="不准确标记" value={stats.inaccurate_count} prefix={<DislikeOutlined />} valueStyle={{ color: '#ff4d4f' }} /></Col>
          <Col span={6}><Statistic title="改进率" value={(stats.improvement_rate * 100).toFixed(0)} suffix="%" prefix={<ThunderboltOutlined />} valueStyle={{ color: '#52c41a' }} /></Col>
        </Row>
      </Card>

      {/* AI 学习沉淀面板 — 管理员在后台对历史邮件做「收录/优先参考/归档」标记后，这些经验会自动注入本模块 LLM 查询 */}
      <Card
        className="card-shadow"
        style={{ marginBottom: 16, borderLeft: '4px solid #3E6AE1', background: 'linear-gradient(90deg,#f7faff 0%,#ffffff 100%)' }}
        title={<span><BulbOutlined style={{ color: '#3E6AE1' }} /> AI 学习沉淀（管理员在后台标记的邮件经验）</span>}
        extra={<a onClick={fetchLearnStats} style={{ fontSize: 12 }}>刷新</a>}
      >
        <Row gutter={[16, 12]}>
          <Col span={4}>
            <Statistic
              title={<span style={{ color: '#52c41a' }}>[已收录]</span>}
              value={learnStats.good}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={<span style={{ color: '#faad14' }}>[优先参考]</span>}
              value={learnStats.preferred}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={<span style={{ color: '#722ed1' }}>[已归档]</span>}
              value={learnStats.archive}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={<span style={{ color: '#ff4d4f' }}>[不准确]</span>}
              value={learnStats.bad}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title={<span style={{ color: '#3E6AE1' }}>[置顶记忆]</span>}
              value={learnStats.pinned}
              suffix="条"
              valueStyle={{ color: '#3E6AE1' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="沉淀总数"
              value={learnStats.total}
              valueStyle={{ fontWeight: 600 }}
            />
          </Col>
        </Row>
        {learnStats.total === 0 && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            管理员在「管理员控制台 - 导入管理 - 策略记忆管理」对邮件点「收录/归档/优先参考」后，经验会出现在这里，<b style={{ color: '#3E6AE1' }}>并在本模块查询策略时自动喂给 AI</b>。
          </Text>
        )}
        {learnStats.total > 0 && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            共 {learnStats.total} 条邮件经验由管理员标记，其中 {learnStats.pinned} 条置顶记忆已写入 LLM system prompt，下次查询自动参考。
          </Text>
        )}
      </Card>

      <Card className="card-shadow" styles={{ body: { padding: 0 } }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'query', label: <span><ThunderboltOutlined /> 策略查询</span>, children: <div style={{ padding: 16 }}>{queryTab}</div> },
            { key: 'import', label: <span><MailOutlined /> 邮件导入</span>, children: <div style={{ padding: 16 }}>{importTab}</div> },
            { key: 'cases', label: <span><DatabaseOutlined /> 策略录入</span>, children: <div style={{ padding: 16 }}>{casesTab}</div> },
            { key: 'stats', label: <span><BulbOutlined /> 学习统计</span>, children: <div style={{ padding: 16 }}>{statsTab}</div> },
          ]}
        />
      </Card>

      {/* 单个邮件导入弹窗 */}
      <Modal
        title="手动导入邮件Thread"
        open={threadModalOpen}
        onCancel={() => setThreadModalOpen(false)}
        width={640}
        footer={null}
      >
        <Form form={threadForm} layout="vertical" onFinish={handleCreateThread}>
          <Form.Item name="subject" label="邮件主题" rules={[{ required: true, message: '请输入主题' }]}>
            <Input placeholder="邮件主题" />
          </Form.Item>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item name="customer_name" label="客户" style={{ flex: 1 }}>
              <Input placeholder="客户名称" />
            </Form.Item>
            <Form.Item name="carrier" label="卡司" style={{ flex: 1 }}>
              <Input placeholder="卡司名称" />
            </Form.Item>
            <Form.Item name="order_no" label="订单号" style={{ flex: 1 }}>
              <Input placeholder="订单号" />
            </Form.Item>
          </Space>
          <Form.Item name="messages" label="邮件内容（多封邮件用 --- 分隔）">
            <TextArea rows={12} placeholder={'格式示例：\n发件人: carrier@example.com\n收件人: dispatch@company.com\n主题: Re: Delay\n正文内容...\n---\n发件人: dispatch@company.com\n收件人: carrier@example.com\n主题: Re: Delay\n回复内容...'} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">导入并分析</Button>
              <Button onClick={() => setThreadModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入弹窗 */}
      <Modal
        title="批量导入历史邮件"
        open={batchModalOpen}
        onCancel={() => setBatchModalOpen(false)}
        width={640}
        footer={[
          <Button key="cancel" onClick={() => setBatchModalOpen(false)}>取消</Button>,
          <Button key="import" type="primary" loading={importing} onClick={handleBatchImport}>开始导入</Button>,
        ]}
      >
        <Paragraph style={{ color: '#666' }}>
          每个邮件thread用 === 分隔。每个thread内可包含：主题、客户、卡司、订单号、正文。
        </Paragraph>
        <TextArea
          rows={14}
          placeholder={'主题: 货物滞留问题\n客户: ABC公司\n卡司: XYZ Trucking\n订单号: BOL123456\n邮件正文内容...\n===\n主题: 另一个案例\n客户: DEF公司\n...'}
          value={batchText}
          onChange={(e) => setBatchText(e.target.value)}
        />
      </Modal>

      {/* 录入/编辑策略案例弹窗 */}
      <Modal
        title={editingCase ? '编辑策略案例' : '录入策略案例'}
        open={caseModalOpen}
        onCancel={() => setCaseModalOpen(false)}
        width={820}
        footer={null}
        destroyOnClose
        centered
        bodyStyle={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px 28px' }}
      >
        <Form form={caseForm} layout="vertical" onFinish={handleCaseSubmit} requiredMark="optional">
          {/* 1. 策略标题 */}
          <Form.Item
            name="title"
            label={<Text strong style={{ fontSize: 14 }}>策略标题 <Text type="danger">*</Text></Text>}
            rules={[{ required: true, message: '请输入策略标题' }]}
          >
            <Input placeholder="如：码头滞留费争议协商话术 —— 合同条款路径" maxLength={200} size="large" />
          </Form.Item>

          {/* 2. 问题类型（16项默认枚举下拉 必填）*/}
          <Form.Item
            name="problem_type"
            label={<Text strong style={{ fontSize: 14 }}>问题类型 <Text type="danger">*</Text></Text>}
            rules={[{ required: true, message: '请选择问题类型' }]}
          >
            <Select
              placeholder={`请选择问题类型（当前有 ${problemTypes.length} 项，如：滞留/破损/拒收/未提取/丢货/时效延误/异常签收/空跑/超重超长/其他...）`}
              allowClear
              size="large"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={problemTypes.map(t => ({ value: t, label: t }))}
            />
          </Form.Item>

          <Divider style={{ margin: '4px 0 16px' }}>📝 内容详情</Divider>

          {/* 4. 场景描述 */}
          <Form.Item
            name="scenario"
            label={<Text strong style={{ fontSize: 14 }}>场景描述（问题背景）</Text>}
          >
            <TextArea
              rows={4}
              showCount
              maxLength={800}
              placeholder="例如：货物在LAX码头滞留超期（>72小时），卡司拒付滞留费；仓库于 9月1日 11:30 通过 Mary 告知司机空提，但司机未到..."
            />
          </Form.Item>

          {/* 5. 最佳策略 + 关键话术 */}
          <Form.Item
            name="best_strategy"
            label={<Text strong style={{ fontSize: 14 }}>最佳策略总结（推荐方案）</Text>}
          >
            <TextArea
              rows={3}
              showCount
              maxLength={600}
              placeholder="该场景下您最推荐的处理方案：如「一般能提供仓库名(Mary)和时间(11:30)时，优先沟通发货人端，同时保留对卡司的正式出函」"
            />
          </Form.Item>

          <Form.Item
            name="key_tactics"
            label={<Text strong style={{ fontSize: 14 }}>关键沟通话术 / 技巧 / 模板要点</Text>}
          >
            <TextArea
              rows={3}
              showCount
              maxLength={600}
              placeholder={
                '■ 客户：需要确认仓库是否存在该情况，确认货物是否备好，并明确客户同意承担空跑费用...\n■ 卡司(英)：Please open a credit note within 24 hours per Clause X of our service contract, failure which the amount will be deducted from the next freight payment.'
              }
            />
          </Form.Item>

          <Divider style={{ margin: '8px 0 16px' }} />

          <Form.Item>
            <Space size={16}>
              <Button type="primary" size="large" htmlType="submit" loading={caseSubmitting}>
                {editingCase ? '💾 保存修改（重新提交审核）' : '📤 提交策略（等待审核）'}
              </Button>
              <Button size="large" onClick={() => setCaseModalOpen(false)}>取消</Button>
              <Button size="large" onClick={() => caseForm.resetFields()}>🔄 重置表单</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
